import { action, makeAutoObservable } from 'mobx';
import { v4 as uuid } from 'uuid';

import type { DashboardStore } from './DashboardStore';
import type { DashboardStory, Story } from '@src/types.ts/story';

const TEMP_ID_FOR_STORY_BEING_CREATED = 'story-from-modal';

// Detailed upload phase (new). Lifecycle: idle -> uploading -> processing -> complete | error
export type StoryUploadPhase =
  | { phase: 'idle' }
  | { phase: 'uploading'; progress: number } // 0-100 inclusive
  | { phase: 'processing'; startedAt: number }
  | { phase: 'complete' }
  | { phase: 'error'; message?: string };

export class UserStoryUploadFlowStore {
  newStoryCompletionPercentRecord: Record<string, number> = {};
  inProgressModalIds: string[] = [];
  bannerHeight: number | undefined = undefined;
  incompleteStoryIds: string[] = [];
  modalIdToStoryId: Record<string, string> = {};
  /** Tracks per-story upload phase (separate from overall completion percent). */
  storyUploadPhaseRecord: Record<string, StoryUploadPhase> = {
    [TEMP_ID_FOR_STORY_BEING_CREATED]: { phase: 'idle' },
  };

  constructor(private dashboardStore: DashboardStore) {
    makeAutoObservable(this, {
      openNewModal: action.bound,
      handleModalClose: action.bound,
      handleNewStoryBlankCreated: action.bound,
      handleNewStoryInitialized: action.bound,
      handleNewStoryCreated: action.bound,
      handleNewStoryProgress: action.bound,
      setStoryUploadPhase: action.bound,
      updateUploadingProgress: action.bound,
      setBannerHeight: action.bound,
      getNewStoryCompletionPercent: action.bound,
      setNewStoryCompletionPercent: action.bound,
    });
  }

  // --- New Phase Helpers ---
  getStoryUploadPhase(storyId?: string): StoryUploadPhase {
    const key = storyId || TEMP_ID_FOR_STORY_BEING_CREATED;
    return this.storyUploadPhaseRecord[key] || { phase: 'idle' };
  }

  setStoryUploadPhase(storyId: string | undefined, phase: StoryUploadPhase) {
    const key = storyId || TEMP_ID_FOR_STORY_BEING_CREATED;
    this.storyUploadPhaseRecord = {
      ...this.storyUploadPhaseRecord,
      [key]: phase,
    };
  }

  updateUploadingProgress(storyId: string | undefined, progress: number) {
    const key = storyId || TEMP_ID_FOR_STORY_BEING_CREATED;
    const clamped = Math.max(0, Math.min(100, Math.round(progress)));
    const current = this.getStoryUploadPhase(storyId);
    if (current.phase === 'uploading' && clamped <= current.progress) return; // ignore regressions
    this.setStoryUploadPhase(key, { phase: 'uploading', progress: clamped });
  }

  startProcessing(storyId?: string) {
    const key = storyId || TEMP_ID_FOR_STORY_BEING_CREATED;
    const current = this.getStoryUploadPhase(storyId);
    // Only move to processing if we have reached (or implied) 100% upload
    if (current.phase === 'uploading' && current.progress < 100) {
      // ensure we record 100 before switching
      this.setStoryUploadPhase(key, { phase: 'uploading', progress: 100 });
    }
    this.setStoryUploadPhase(key, {
      phase: 'processing',
      startedAt: Date.now(),
    });
  }

  markComplete(storyId?: string) {
    const key = storyId || TEMP_ID_FOR_STORY_BEING_CREATED;
    this.setStoryUploadPhase(key, { phase: 'complete' });
  }

  markError(message: string, storyId?: string) {
    const key = storyId || TEMP_ID_FOR_STORY_BEING_CREATED;
    this.setStoryUploadPhase(key, { phase: 'error', message });
  }

  /** When a temp story gains a real id, transfer its phase entry */
  transferTempPhaseToStoryId(realStoryId: string) {
    const tempPhase =
      this.storyUploadPhaseRecord[TEMP_ID_FOR_STORY_BEING_CREATED];
    if (tempPhase) {
      this.storyUploadPhaseRecord = {
        ...this.storyUploadPhaseRecord,
        [realStoryId]: tempPhase,
      };
    }
  }

  // --- Existing Logic (unchanged) ---
  handleModalClose(modalId: string) {
    // premature closing should hide banner
    this.handleNewStoryProgress(0, TEMP_ID_FOR_STORY_BEING_CREATED, true);

    // mark associated story as cancelled
    this.cancelIncompleteStory(this.modalIdToStoryId[modalId]);

    this.closeModal(modalId);
  }

  handleNewStoryBlankCreated(storyId: string, modalId: string) {
    this.addIncompleteStory(storyId);
    this.associateModalWithStory(modalId, storyId);

    if (!this.isInProgress(modalId)) {
      console.warn(
        'Story was created after the corresponding modal was prematurely closed',
        {
          storyId,
          modalId,
        },
      );
      this.cancelIncompleteStory(storyId);
    }
  }

  handleNewStoryInitialized(story: Story, modalId: string) {
    const newStory = story as unknown as DashboardStory;
    // transfer progress from temp id to created story id, will hide banner
    this.handleNewStoryProgress(
      this.getNewStoryCompletionPercent(),
      newStory.id,
      true,
    );
    this.transferTempPhaseToStoryId(newStory.id);
    this.addIncompleteStory(newStory.id);
    this.dashboardStore.addStory(newStory);
    this.associateModalWithStory(modalId, newStory.id);
  }

  handleNewStoryCreated(story: Story, modalId: string) {
    const newStory = story as unknown as DashboardStory;
    this.handleNewStoryProgress(
      Math.max(50, this.getNewStoryCompletionPercent(newStory.id)),
      newStory.id,
      true,
    );
    this.removeIncompleteStory(newStory.id);
    this.dashboardStore.updateStory(newStory);
    this.markComplete(newStory.id);
    this.closeModal(modalId);
  }

  handleNewStoryProgress(percent: number, storyId?: string, reset?: boolean) {
    const currentCompletionRecord = this.newStoryCompletionPercentRecord;
    const updatedRecord = {
      ...currentCompletionRecord,
    };
    if (
      storyId &&
      percent >
        this.getNewStoryCompletionPercent(storyId, currentCompletionRecord)
    ) {
      updatedRecord[storyId] = percent;
    }
    if (
      !storyId &&
      percent >
        this.getNewStoryCompletionPercent(storyId, currentCompletionRecord)
    ) {
      updatedRecord[TEMP_ID_FOR_STORY_BEING_CREATED] = percent;
    }
    if (reset) {
      updatedRecord[TEMP_ID_FOR_STORY_BEING_CREATED] = 0;
    }
    this.setNewStoryCompletionPercentRecord(updatedRecord);
  }

  getNewStoryCompletionPercent(
    storyId?: string,
    currentCompletionRecord?: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  ): number {
    const completionRecord =
      currentCompletionRecord || this.newStoryCompletionPercentRecord;
    const key = storyId || TEMP_ID_FOR_STORY_BEING_CREATED;
    if (key in completionRecord) {
      return completionRecord[key];
    } else {
      return 0;
    }
  }

  setNewStoryCompletionPercent(storyId: string, percent: number) {
    this.setNewStoryCompletionPercentRecord({
      ...this.newStoryCompletionPercentRecord,
      [storyId]: percent,
    });
  }

  openNewModal() {
    const modalId = uuid();
    this.setInProgressModalIds([...this.inProgressModalIds, modalId]);
  }

  closeModal(modalId: string) {
    this.setInProgressModalIds(
      this.inProgressModalIds.filter(id => id !== modalId),
    );
    this.removeModalStoryAssociation(modalId);
  }

  setNewStoryCompletionPercentRecord(record: Record<string, number>) {
    this.newStoryCompletionPercentRecord = record;
  }

  setInProgressModalIds(ids: string[]) {
    this.inProgressModalIds = ids;
  }

  isInProgress(modalId: string): boolean {
    return this.inProgressModalIds.includes(modalId);
  }

  setIncompleteStoryIds(ids: string[]) {
    this.incompleteStoryIds = ids;
  }

  private async cancelIncompleteStory(storyId?: string) {
    if (!storyId) {
      console.warn('No story ID provided for cancellation');
      return;
    }
    const storiesToCancel = this.incompleteStoryIds.filter(s => s === storyId);
    const updatePromises = storiesToCancel.map(async storyId => {
      const story = this.dashboardStore.allStories.find(s => s.id === storyId);
      if (story) {
        const updatedStory = {
          ...story,
          externalUploadStatus: 'CANCELLED' as const,
        };
        this.dashboardStore.updateStory(updatedStory);
      }
      try {
        await this.dashboardStore.saveStory({
          id: storyId,
          externalUploadStatus: 'CANCELLED',
        });
      } catch (error) {
        console.error(`Failed to cancel story ${storyId} on server:`, error);
      }
    });
    await Promise.allSettled(updatePromises);
    this.removeIncompleteStory(storyId);
  }

  setBannerHeight(height: number | undefined) {
    this.bannerHeight = height;
  }

  private addIncompleteStory(newStoryId: string) {
    if (!this.incompleteStoryIds.includes(newStoryId)) {
      this.setIncompleteStoryIds([...this.incompleteStoryIds, newStoryId]);
    }
  }

  private removeIncompleteStory(newStoryId: string) {
    this.setIncompleteStoryIds(
      this.incompleteStoryIds.filter(id => id !== newStoryId),
    );
  }

  private associateModalWithStory(modalId: string, storyId: string) {
    this.modalIdToStoryId[modalId] = storyId;
  }

  private removeModalStoryAssociation(modalId: string) {
    delete this.modalIdToStoryId[modalId];
  }
}
